import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

export interface ContextMenuItem {
  label: string;
  destructive?: boolean;
  onSelect: () => void;
}

interface ContextMenuProps {
  items: ContextMenuItem[];
  ariaLabel?: string;
}

interface MenuCoords {
  top: number;
  left: number;
  ready: boolean;
}

const EDGE_MARGIN = 8;
const GAP = 4;

function calcCoords(anchorRect: DOMRect, menuEl: HTMLUListElement): MenuCoords {
  const { innerWidth: vw, innerHeight: vh } = window;
  const menuH = menuEl.offsetHeight;
  const menuW = menuEl.offsetWidth;

  const spaceBelow = vh - anchorRect.bottom - EDGE_MARGIN;
  const spaceAbove = anchorRect.top - EDGE_MARGIN;
  const top =
    spaceBelow >= menuH || spaceBelow >= spaceAbove
      ? anchorRect.bottom + GAP
      : anchorRect.top - menuH - GAP;

  let left = anchorRect.right - menuW;
  if (left < EDGE_MARGIN) left = EDGE_MARGIN;
  if (left + menuW > vw - EDGE_MARGIN) left = vw - EDGE_MARGIN - menuW;

  return { top, left, ready: true };
}

export function ContextMenu({ items, ariaLabel = "Mais ações" }: ContextMenuProps) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<MenuCoords>({ top: -9999, left: -9999, ready: false });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLUListElement>(null);

  const close = useCallback(() => setOpen(false), []);

  const recompute = useCallback(() => {
    if (!buttonRef.current || !menuRef.current) return;
    setCoords(calcCoords(buttonRef.current.getBoundingClientRect(), menuRef.current));
  }, []);

  // Measure and position the menu after it mounts in the portal
  useLayoutEffect(() => {
    if (!open) return;
    if (!buttonRef.current || !menuRef.current) return;
    setCoords(calcCoords(buttonRef.current.getBoundingClientRect(), menuRef.current));
  }, [open]);

  // Close on outside click / Escape; recompute on scroll/resize
  useEffect(() => {
    if (!open) return;

    const onMouseDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (buttonRef.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      close();
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") close();
    };
    const onScroll = () => recompute();
    const onResize = () => recompute();

    document.addEventListener("mousedown", onMouseDown);
    window.addEventListener("keydown", onKey);
    window.addEventListener("resize", onResize);
    // capture:true to catch scroll inside overflow-auto containers
    window.addEventListener("scroll", onScroll, { capture: true, passive: true });

    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onScroll, { capture: true });
    };
  }, [open, close, recompute]);

  const handleButtonClick = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (!open) {
      setCoords({ top: -9999, left: -9999, ready: false });
    }
    setOpen((prev) => !prev);
  };

  const menu = open
    ? createPortal(
        <ul
          ref={menuRef}
          role="menu"
          style={{
            position: "fixed",
            top: coords.top,
            left: coords.left,
            visibility: coords.ready ? "visible" : "hidden",
          }}
          className="z-[9999] min-w-[9rem] rounded-md border border-ink-600 bg-ink-900 py-1 shadow-lg"
        >
          {items.map((item) => (
            <li key={item.label} role="presentation">
              <button
                type="button"
                role="menuitem"
                className={`w-full px-3 py-2 text-left text-sm hover:bg-ink-800 ${
                  item.destructive ? "text-red-400 hover:bg-red-950/40" : "text-ink-200"
                }`}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  close();
                  item.onSelect();
                }}
              >
                {item.label}
              </button>
            </li>
          ))}
        </ul>,
        document.body,
      )
    : null;

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        aria-label={ariaLabel}
        aria-expanded={open}
        aria-haspopup="menu"
        className="rounded p-1 text-ink-400 hover:bg-ink-700 hover:text-ink-100"
        onClick={handleButtonClick}
      >
        <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <circle cx="5" cy="12" r="2" />
          <circle cx="12" cy="12" r="2" />
          <circle cx="19" cy="12" r="2" />
        </svg>
      </button>
      {menu}
    </>
  );
}
